/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy at http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND.
 */
import { calculateQuote, DEMO_EMAIL_CODE } from './booking.js';
import { hotels } from './catalog.js';

const env = import.meta.env;
export const demoMode = env.VITE_DEMO_MODE !== 'false';
let authPromise;

// These mirror INTENT_TTL_SECONDS and the step-up Lambda defaults.
const DEMO_INTENT_TTL_MS = 15 * 60_000;
const DEMO_CHALLENGE_TTL_MS = 5 * 60_000;
const DEMO_ELEVATION_TTL_MS = 5 * 60_000;
const DEMO_MAX_ATTEMPTS = 3;

async function getAuth() {
  if (!authPromise) {
    authPromise = Promise.all([import('aws-amplify'), import('aws-amplify/auth')]).then(
      ([{ Amplify }, auth]) => {
        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: env.VITE_USER_POOL_ID,
              userPoolClientId: env.VITE_USER_POOL_CLIENT_ID,
            },
          },
        });
        return auth;
      },
    );
  }
  return authPromise;
}

const demoState = { intent: undefined, verified: false, attempts: 0 };

async function authorizedFetch(path, options = {}) {
  if (!env.VITE_API_KEY) {
    throw new Error('VITE_API_KEY is required when AWS-connected mode is enabled.');
  }
  const { fetchAuthSession } = await getAuth();
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  if (!accessToken) throw new Error('Your session has expired. Sign in again.');

  const response = await fetch(`${env.VITE_API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'x-api-key': env.VITE_API_KEY,
      ...options.headers,
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.message || 'The request could not be completed.');
    error.status = response.status;
    error.code = body.code;
    throw error;
  }
  return body;
}

export async function authenticate(email, password) {
  if (demoMode) return { email: email || 'traveler@example.com' };
  const { signIn } = await getAuth();
  const result = await signIn({ username: email, password });
  if (!result.isSignedIn) {
    throw new Error(`Complete the Cognito sign-in step: ${result.nextStep.signInStep}.`);
  }
  return { email };
}

export async function endSession() {
  if (!demoMode) {
    const { signOut } = await getAuth();
    await signOut();
  }
}

export async function getCatalog() {
  if (demoMode) return { hotels };
  return authorizedFetch('/catalog');
}

export async function createBookingIntent(selection) {
  if (demoMode) {
    const quote = calculateQuote(selection);
    demoState.intent = {
      bookingIntentId: crypto.randomUUID(),
      ...quote,
      expiresAt: new Date(Date.now() + DEMO_INTENT_TTL_MS).toISOString(),
    };
    demoState.verified = false;
    demoState.attempts = 0;
    return demoState.intent;
  }
  return authorizedFetch('/booking-intents', {
    method: 'POST',
    body: JSON.stringify(selection),
  });
}

export async function confirmBooking(intentId) {
  if (demoMode) {
    if (!demoState.intent || demoState.intent.bookingIntentId !== intentId) {
      throw new Error('The booking quote has expired.');
    }
    if (demoState.intent.stepUpRequired && !demoState.verified) {
      const error = new Error('Confirm your identity to finish this booking.');
      error.status = 401;
      error.code = 'STEP_UP_REQUIRED';
      throw error;
    }
    return {
      bookingId: `AH-${crypto.randomUUID().slice(0, 8).toUpperCase()}`,
      status: 'CONFIRMED',
      bookingIntentId: intentId,
      total: demoState.intent.total,
      confirmedAt: new Date().toISOString(),
    };
  }
  return authorizedFetch(`/booking-intents/${encodeURIComponent(intentId)}/confirm`, {
    method: 'POST',
  });
}

export async function requestEmailChallenge(intentId) {
  if (demoMode) {
    if (!demoState.intent || demoState.intent.bookingIntentId !== intentId) {
      throw new Error('The booking quote has expired.');
    }
    return {
      challengeId: crypto.randomUUID(),
      channel: 'EMAIL',
      maskedDestination: 't******r@example.com',
      expiresAt: new Date(Date.now() + DEMO_CHALLENGE_TTL_MS).toISOString(),
      demoCode: DEMO_EMAIL_CODE,
    };
  }
  return authorizedFetch('/step-up/challenges', {
    method: 'POST',
    body: JSON.stringify({ bookingIntentId: intentId }),
  });
}

export async function verifyEmailChallenge(challengeId, code) {
  if (demoMode) {
    demoState.attempts += 1;
    if (code !== DEMO_EMAIL_CODE) {
      if (demoState.attempts >= DEMO_MAX_ATTEMPTS) throw new Error('Maximum attempts reached.');
      throw new Error('That code did not match. Try again.');
    }
    demoState.verified = true;
    return {
      status: 'VERIFIED',
      validUntil: new Date(Date.now() + DEMO_ELEVATION_TTL_MS).toISOString(),
    };
  }
  return authorizedFetch(`/step-up/challenges/${encodeURIComponent(challengeId)}/verify`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}
